import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Send, Paperclip, MoreVertical, Check, CheckCheck, Clock } from 'lucide-react';
import { apiService } from '../../services/api';
import { useSocket } from '../../contexts/SocketContext';
import { notificationService } from '../../services/notificationService';

interface Message {
  _id: string;
  content: string;
  senderType: 'admin' | 'applicant';
  sender: {
    admin?: {
      _id: string;
      email: string;
      profile?: {
        firstName: string;
        lastName: string;
      };
    };
    applicant?: {
      name: string;
      email: string;
    };
  };
  sentAt: string;
  isRead: boolean;
  attachments?: any[];
}

interface Thread {
  _id: string;
  application?: {
    _id: string;
    shopNumber?: string;
    ShopDetails?: {
      ShopNumber?: string;
    };
    status: string;
  };
  applicant: {
    name: string;
    email: string;
  };
  admin?: {
    _id: string;
    email: string;
    profile?: {
      firstName: string;
      lastName: string;
    };
  };
  status: string;
  lastMessageAt: string;
  unreadCount: number;
}

const AdminMessages: React.FC = () => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  
  const { 
    isConnected, 
    joinThread, 
    leaveThread, 
    sendTypingStart, 
    sendTypingStop, 
    onNewMessage, 
    onUserTyping, 
    onUserStoppedTyping,
    offNewMessage,
    offUserTyping,
    offUserStoppedTyping
  } = useSocket();

  useEffect(() => {
    fetchThreads();
    requestNotificationPermission();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchThreadMessages(selectedThread._id);
      joinThread(selectedThread._id);
      
      // Set up real-time listeners
      onNewMessage(handleNewMessage);
      onUserTyping(handleUserTyping);
      onUserStoppedTyping(handleUserStoppedTyping);
      
      return () => {
        leaveThread(selectedThread._id);
        offNewMessage();
        offUserTyping();
        offUserStoppedTyping();
      };
    }
  }, [selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const requestNotificationPermission = async () => {
    await notificationService.requestPermission();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchThreads = async () => {
    try {
      console.log('=== FRONTEND: FETCHING MESSAGE THREADS ===');
      const response = await apiService.getMessageThreads();
      console.log('API Response:', response);
      
      if (response.status === 'success') {
        console.log('Raw threads:', response.data.threads);
        setThreads(response.data.threads);
      } else {
        console.error('API returned error:', response);
        setError('Failed to load message threads');
      }
    } catch (err) {
      console.error('API call failed:', err);
      setError('Failed to load message threads');
    } finally {
      setLoading(false);
    }
  };

  const fetchThreadMessages = async (threadId: string) => {
    try {
      const response = await apiService.getThreadMessages(threadId);
      if (response.status === 'success') {
        setMessages(response.data.messages);
      } else {
        setError('Failed to load messages');
      }
    } catch (err) {
      setError('Failed to load messages');
    }
  };

  const handleNewMessage = (data: any) => {
    console.log('New message received:', data);
    
    if (data.threadId === selectedThread?._id) {
      setMessages(prev => [...prev, data.message]);
      
      // Show notification if message is from applicant
      if (data.senderType === 'applicant') {
        const senderName = data.message.sender.applicant?.name || 'Applicant';
        notificationService.showMessageNotification(senderName, data.message.content, data.threadId);
      }
    }
    
    // Update thread list with new unread count
    setThreads(prev => prev.map(thread => {
      if (thread._id === data.threadId) {
        return {
          ...thread,
          lastMessageAt: data.message.sentAt,
          unreadCount: data.senderType === 'applicant' ? thread.unreadCount + 1 : thread.unreadCount
        };
      }
      return thread;
    }));
  };

  const handleUserTyping = (data: any) => {
    setTypingUsers(prev => new Set([...prev, data.userName]));
  };

  const handleUserStoppedTyping = (data: any) => {
    setTypingUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(data.userName);
      return newSet;
    });
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    if (!isTyping && selectedThread) {
      setIsTyping(true);
      sendTypingStart(selectedThread._id, 'Admin', 'admin');
    }
    
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    const timeout = setTimeout(() => {
      setIsTyping(false);
      if (selectedThread) {
        sendTypingStop(selectedThread._id, 'Admin', 'admin');
      }
    }, 1000);
    
    setTypingTimeout(timeout);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedThread || sending) return;

    setSending(true);
    try {
      const response = await apiService.sendMessageToApplicant(selectedThread._id, newMessage.trim());
      if (response.status === 'success') {
        setNewMessage('');
        // Don't add message manually - it will come through real-time event
        
        // Stop typing indicator
        if (isTyping) {
          setIsTyping(false);
          sendTypingStop(selectedThread._id, 'Admin', 'admin');
        }
      } else {
        setError('Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const assignToSelf = async (threadId: string) => {
    try {
      await apiService.assignAdminToThread(threadId);
      fetchThreads();
      if (selectedThread?._id === threadId) {
        fetchThreadMessages(threadId);
      }
    } catch (err) {
      setError('Failed to assign thread');
    }
  };

  const getSenderName = (message: Message) => {
    if (message.senderType === 'admin' && message.sender.admin) {
      const profile = message.sender.admin.profile;
      if (profile?.firstName && profile?.lastName) {
        return `${profile.firstName} ${profile.lastName}`;
      }
      return message.sender.admin.email;
    } else if (message.senderType === 'applicant') {
      return message.sender.applicant?.name || 'Applicant';
    }
    return 'Unknown';
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageStatus = (message: Message) => {
    if (message.senderType === 'admin') {
      if (message.isRead) {
        return <CheckCheck className="w-4 h-4 text-green-500" />;
      } else {
        return <Check className="w-4 h-4 text-gray-400" />;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Message Center</h1>
          <p className="text-gray-600 mt-2">Communicate with applicants about their applications</p>
          <div className="flex items-center mt-2">
            <div className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-500">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-200px)] flex">
          {/* Threads Sidebar */}
          <div className="w-1/3 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No conversations yet</p>
                  <p className="text-sm text-gray-400 mb-4">
                    Message threads will appear here when applicants start conversations or when you initiate them from the Applications page.
                  </p>
                  <Link
                    to="/admin/applications"
                    className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                  >
                    View Applications
                  </Link>
                </div>
              ) : (
                threads.map((thread) => (
                  <div
                    key={thread._id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-100 ${
                      selectedThread?._id === thread._id ? 'bg-green-50 border-r-2 border-primary' : ''
                    }`}
                    onClick={() => setSelectedThread(thread)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {thread.applicant.name}
                          </p>
                          {thread.unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                              {thread.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {thread.application ? 
                            `${thread.application.shopNumber || thread.application.ShopDetails?.ShopNumber || 'N/A'} - ${thread.application.status}` : 
                            `Thread ${thread._id.slice(-6)}`
                          }
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(thread.lastMessageAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {selectedThread ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {selectedThread.applicant.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {selectedThread.application ? 
                          `${selectedThread.application.shopNumber || selectedThread.application.ShopDetails?.ShopNumber || 'N/A'} - ${selectedThread.application.status}` : 
                          `Thread ${selectedThread._id.slice(-6)}`
                        }
                      </p>
                    </div>
                    {!selectedThread.admin && (
                      <button
                        onClick={() => assignToSelf(selectedThread._id)}
                        className="bg-primary text-white px-3 py-1 rounded-md text-sm hover:bg-primary-dark transition-colors"
                      >
                        Take Over
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.map((message) => (
                    <div
                      key={message._id}
                      className={`flex ${message.senderType === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.senderType === 'admin'
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-900 border border-gray-200'
                        }`}
                      >
                        <p className="text-sm">{message.content}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${message.senderType === 'admin' ? 'text-green-100' : 'text-gray-500'}`}>
                            {formatTime(message.sentAt)}
                          </span>
                          {getMessageStatus(message)}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing Indicator */}
                  {typingUsers.size > 0 && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                        <div className="flex items-center space-x-1">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {Array.from(typingUsers).join(', ')} typing...
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-gray-200 bg-white">
                  <form onSubmit={sendMessage} className="flex items-center space-x-2">
                    <button
                      type="button"
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <input
                      ref={messageInputRef}
                      type="text"
                      value={newMessage}
                      onChange={handleTyping}
                      placeholder="Type your message..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="p-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {sending ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
                  <p className="text-gray-500">Choose a conversation from the sidebar to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;